import React from "react";

import { ReferencePickerPlugin } from "@adsk/acc-reference-picker";
import { TOOL_IDS } from "@adsk/alloy-provider";
import { FanIcon } from "@adsk/alloy-react-icon";
import { useLocale } from "@adsk/alloy-react-locale";
import { TabTitle } from "@adsk/exo-skeleton-react";

import { clients } from "../../index";
import { SystemWithCategoryAndStatus } from "../../models";
import getParsedSystems from "../helpers/getParsedSystems";
import getUniqueValues from "../helpers/getUniqueValues";
import SystemsActivityCard from "./SystemsActivityCard";
import SystemsCustomDataView from "./SystemsCustomDataView";
import SystemsEntityBlock from "./SystemsEntityBlock";
import SystemsEntityMenuItem from "./SystemsEntityMenuItem";
import SystemsPickerEmptyState from "./SystemsPickerEmptyState";
import SystemsPickerNoSearchResults from "./SystemsPickerNoSearchResults";
import SystemsPickerSearchBox from "./SystemsPickerSearchBox";
import SystemsProvider from "./SystemsProvider";
const NestedOptionMenuLabel = () => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  return <>{T.translate("ASSETS")}</>;
};

const plugin: ReferencePickerPlugin<SystemWithCategoryAndStatus> = {
  nestedOptions: {
    label: <NestedOptionMenuLabel />,
    renderIcon: (props) => <FanIcon {...props} />,
    key: "assets",
    nestedCategoryKey: "systems",
  },
  metadata: {
    domain: "autodesk-bim360-asset",
    type: "system",
  },
  renderIcon: (p) => <FanIcon {...p} />,
  toolId: TOOL_IDS.ASSETS,
  showSearchBox: true,
  is3LOSupported: true,
  queryEntities: async ({
    projectId,
    pageLimit,
    filters: { searchExpression, cursorState },
  }) => {
    const { results: systems, pagination } = await clients
      .getSystemClient(projectId)
      .query({
        includeFilters: { searchText: searchExpression },
        limit: pageLimit,
        cursorState: cursorState as string | undefined,
      });

    if (systems.length === 0) {
      return { data: [] };
    }

    const categoryIds = getUniqueValues(systems, "categoryId");
    const uniqueStatusesIds = getUniqueValues(systems, "statusId");

    const [categories, statuses] = await Promise.all([
      clients
        .getCategoryClient(projectId)
        .batchGetById(categoryIds, "system")
        .catch(() => []),
      clients
        .getStatusSetClient(projectId)
        .batchGetStatuses(uniqueStatusesIds)
        .catch(() => []),
    ]);

    const statusSetAssignments = await clients
      .getCategoryClient(projectId)
      .batchGetStatusSetAssignmentsV3(
        categories.map((category) => category.id),
        "system",
        true
      );

    return {
      data: getParsedSystems(
        systems,
        categories,
        statuses,
        statusSetAssignments
      ),
      nextPageQuery: pagination.nextPost?.url
        ? {
            projectId,
            pageLimit,
            filters: { cursorState: pagination.cursorState },
          }
        : undefined,
    };
  },
  PluralText: ({ lowerCase }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const result = T.translate("SYSTEMS");
    if (lowerCase && typeof result === "string") {
      return result.toLowerCase();
    }
    return result;
  },
  PickerModalTitle: ({ showTabTitle = true }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const title = T.translate("ADD_SYSTEM_REFERENCE");
    return (
      <>
        {showTabTitle && <TabTitle value={title as string} />}
        {title}
      </>
    );
  },
  AddRelationshipsButtonText: ({ count }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    return T.translate("ADD_SYSTEMS", { context: count });
  },
  SelectedCount: ({ count }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    return T.translate("SYSTEMS_SELECTED", { context: count });
  },
  CustomRemoveEntityModalTitle: () => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    return T.translate("REMOVE_SYSTEM_REFERENCE_TITLE");
  },
  CustomRemoveEntityModalContent: () => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    return T.translate("REMOVE_SYSTEM_REFERENCE_CONTENT");
  },
  getEntitiesByIds: async ({ projectId, ids }) => {
    const { results: systems } = await clients
      .getSystemClient(projectId)
      .query({
        includeFilters: { ids },
        limit: ids.length,
      });

    const uniqueCategoriesIds = getUniqueValues(systems, "categoryId");
    const uniqueStatusesIds = getUniqueValues(systems, "statusId");

    const [categories, statuses] = await Promise.all([
      clients
        .getCategoryClient(projectId)
        .batchGetById(uniqueCategoriesIds, "system")
        .catch(() => []),
      clients
        .getStatusSetClient(projectId)
        .batchGetStatuses(uniqueStatusesIds)
        .catch(() => []),
    ]);

    return { data: getParsedSystems(systems, categories, statuses) };
  },
  Provider: SystemsProvider,
  EntityBlock: SystemsEntityBlock,
  EntityMenuItem: SystemsEntityMenuItem,
  ActivityCard: SystemsActivityCard,
  CustomDataView: SystemsCustomDataView,
  CustomPickerSearchBox: SystemsPickerSearchBox,
  CustomPickerEmptyState: SystemsPickerEmptyState,
  CustomPickerNoSearchResults: SystemsPickerNoSearchResults,
};

export default plugin;
